// src/ui/Menu.tsx (T1): a small action menu (the room menu: Rename, Not for us, Delete; the project switcher).
// The trigger is rendered by the caller through `trigger`, which receives the props to spread on a button.
// Arrow keys move, Enter or Space picks, Esc or a tap outside closes. Root class: hb-menu.
import type { ComponentChildren } from "preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { navigate } from "../app/router";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";
import { useDismiss, usePopoverPlacement } from "./popover";

export type MenuItem = {
  label: ComponentChildren;
  /** A short second line ("Near the lake · Your land"). */
  sub?: ComponentChildren;
  icon?: IconName;
  onSelect?: () => void;
  /** Navigates instead (app path). */
  href?: string;
  current?: boolean;
  disabled?: boolean;
  /** A destructive action (Delete room). */
  danger?: boolean;
};

export type MenuTriggerProps = {
  ref: (el: HTMLElement | null) => void;
  "aria-haspopup": "menu";
  "aria-expanded": "true" | "false";
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
};

export function Menu(props: {
  label: string;
  items: readonly MenuItem[];
  trigger: (p: MenuTriggerProps) => ComponentChildren;
  align?: "start" | "end";
  class?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLElement | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const list = useRef<HTMLDivElement>(null);
  // Preact never passes `ref` through a function component, so a trigger such as <IconButton {...p}/> hands
  // the ref callback nothing (or a component instance). The trigger is always the wrapper's first element.
  useLayoutEffect(() => {
    const first = wrap.current?.firstElementChild;
    if (!(anchor.current instanceof HTMLElement) && first instanceof HTMLElement && first !== list.current) anchor.current = first;
  });
  usePopoverPlacement(open, anchor, list, props.align ?? "start");
  useDismiss(open, [anchor, list], () => close(false));

  function focusItem(i: number) {
    const items = list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
    if (!items || items.length === 0) return;
    const n = ((i % items.length) + items.length) % items.length;
    items[n]!.focus();
  }
  function openMenu(at: number) {
    setOpen(true);
    requestAnimationFrame(() => focusItem(at));
  }
  function close(refocus: boolean) {
    setOpen(false);
    if (refocus) anchor.current?.focus();
  }
  function pick(item: MenuItem) {
    if (item.disabled) return;
    close(true);
    if (item.href) navigate(item.href);
    item.onSelect?.();
  }
  const onListKey = (e: KeyboardEvent) => {
    const items = Array.from(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusItem(i + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusItem(i - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusItem(-1);
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  return (
    <span ref={wrap} class={cx("hb-menu", props.class)}>
      {props.trigger({
        ref: (el) => {
          if (el instanceof HTMLElement) anchor.current = el;
        },
        "aria-haspopup": "menu",
        "aria-expanded": open ? "true" : "false",
        onClick: () => (open ? close(false) : openMenu(0)),
        onKeyDown: (e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            openMenu(e.key === "ArrowDown" ? 0 : -1);
          }
        },
      })}
      <div ref={list} class="hb-menu-list" role="menu" aria-label={props.label} hidden={!open} onKeyDown={onListKey}>
        {props.items.map((item, i) => (
          <button
            key={i}
            type="button"
            role="menuitem"
            tabIndex={-1}
            class={cx("hb-menu-item", item.danger && "is-danger", item.current && "is-current")}
            aria-disabled={item.disabled ? "true" : undefined}
            aria-current={item.current ? "true" : undefined}
            onClick={() => pick(item)}
          >
            {item.icon ? <Icon name={item.icon} size="sm" /> : null}
            <span class="hb-menu-t">
              <span>{item.label}</span>
              {item.sub ? <span class="hb-menu-sub">{item.sub}</span> : null}
            </span>
            {item.current ? <Icon name="check" size="sm" class="hb-menu-check" /> : null}
          </button>
        ))}
      </div>
    </span>
  );
}
